import * as React from "react";
import { useEffect, useState } from "react";
import { format } from "date-fns";
import {
  MessageSquare,
  RefreshCw,
  Download,
  Search,
  Filter,
  Eye,
  AlertCircle,
  Smartphone,
} from "lucide-react";
import AntDTable from "@/common/components/ui/antd-table";
import { Card, CardContent } from "@/common/components/ui/card";
import { Badge } from "@/common/components/ui/badge";
import { Button } from "@/common/components/ui/button";
import { Input } from "@/common/components/ui/input";
import CommonModal from "@/common/components/ui/modal";
import * as XLSX from "xlsx";
import { notify } from "@/common/components/common/Notification";
import LogsService from "@/services/FinEaseHomeAndAdmin/APIs/LogsService";
import { useToast } from "@/hooks/use-toast";

const SmsLogs = () => {
  const [loading, setLoading] = useState(false);
  const [smsLogs, setSmsLogs] = useState<any[]>([]);
  const [selectedLog, setSelectedLog] = useState<any | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [smsPagination, setSmsPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const { toast } = useToast();

  const fetchLogs = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const res: any = await LogsService.getSmsLogsAPI(page, pageSize);
      const data = res.results ? res : res.data;
      if (data?.results) {
        setSmsLogs(data.results);
        setSmsPagination({
          current: data.page,
          pageSize: data.page_size,
          total: data.count,
        });
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error fetching SMS logs",
        description: error.message ?? "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(smsPagination.current, smsPagination.pageSize);
  }, []);

  const handleRefresh = () => {
    fetchLogs(smsPagination.current, smsPagination.pageSize);
    toast({ title: "Refreshed", description: "SMS logs updated." });
  };

  const handlePageChange = (page: number, pageSize: number) => {
    fetchLogs(page, pageSize);
  };

  const formatDate = (dateUnparsed: string) => {
    try {
      return format(new Date(dateUnparsed), "MMM dd, yyyy HH:mm:ss");
    } catch (_) {
      return dateUnparsed;
    }
  };

  const onSelectChange = (keys: React.Key[], rows: any[]) => {
    setSelectedRowKeys(keys);
    setSelectedRows(rows);
  };

  const handleExport = () => {
    if (selectedRows.length === 0) {
      notify.error("No Data", "Please select items to export.");
      return;
    }

    const rows = selectedRows.map(item => ({
      Timestamp: item.created_on ? format(new Date(item.created_on), "MMM dd, yyyy HH:mm:ss") : "-",
      Phone: item.phone_number ?? "-",
      Recipient: item.user_email ?? item.user ?? "-",
      Template: item.template_name ?? "-",
      Provider: item.provider ?? "-",
      Status: item.status ?? "-",
      Message: item.message_body ?? "-",
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "SmsLogs");

    XLSX.writeFile(
      wb,
      `sms_logs_${new Date().toISOString().split("T")[0]}.xlsx`
    );
    notify.success("Exported", `${rows.length} record(s) exported.`);
  };

  const smsColumns = [
    {
      title: "Timestamp",
      dataIndex: "created_on",
      key: "created_on",
      width: 220,
      render: (text: string) => (
        <span className="font-medium text-gray-700 text-sm">{formatDate(text)}</span>
      ),
    },
    {
      title: "Recipient",
      key: "recipient",
      width: 260,
      render: (_: any, record: any) => (
        <div className="flex flex-col gap-0.5">
          <span className="font-medium text-gray-900">{record.phone_number ?? "N/A"}</span>
          <span className="text-xs text-muted-foreground">
            {record.user_email ?? record.user ?? "Unknown User"}
          </span>
        </div>
      ),
    },
    {
      title: "Template",
      dataIndex: "template_name",
      key: "template_name",
      render: (text: string) => (
        <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">
          {text ?? "NA"}
        </Badge>
      ),
    },
    {
      title: "Provider",
      dataIndex: "provider",
      key: "provider",
      render: (text: string) => (
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-600">
          {text ?? "NA"}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => {
        const normalized = String(status ?? "").toLowerCase();
        const isSuccess = ["sent", "delivered", "success"].includes(normalized);
        return (
          <Badge
            className={
              isSuccess
                ? "border-green-200 bg-green-100 text-green-700"
                : "border-red-200 bg-red-100 text-red-700"
            }
          >
            {status ?? "UNKNOWN"}
          </Badge>
        );
      },
    },
    {
      title: "Details",
      key: "action",
      align: "right" as const,
      width: 90,
      render: (_: any, record: any) => (
        <Button
          variant="ghost"
          size="sm"
          className="hover:bg-primary/10 hover:text-primary"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedLog(record);
          }}
        >
          <Eye className="mr-1.5 h-4 w-4" />
          View
        </Button>
      ),
    },
  ];

  return (
    <>
      <div className="min-h-screen space-y-8 bg-gray-50/50 p-6 font-sans">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 p-6 md:p-8">
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
          <div className="absolute right-0 top-0 opacity-10">
            <MessageSquare className="h-48 w-48 -translate-y-8 translate-x-12 transform text-white" />
          </div>

          <div className="relative flex flex-col justify-between gap-4 md:flex-row md:items-center">
            <div className="flex items-center gap-4">
              <div className="rounded-xl bg-white/10 p-3 backdrop-blur-sm">
                <Smartphone className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="font-display text-2xl font-bold text-white">SMS Logs</h1>
                <p className="text-white/70">
                  Track outbound SMS delivery events and provider responses.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                className="border-white/20 bg-white/10 text-white backdrop-blur-md transition-all hover:bg-white/20 hover:text-white"
                onClick={handleRefresh}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Sync Data
              </Button>
              <Button
                variant="outline"
                className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
                onClick={handleExport}
                disabled={selectedRows.length === 0}
              >
                <Download className="h-4 w-4" />
                Export
                {selectedRows.length > 0 && (
                  <span className="ml-1 rounded-full bg-white/25 px-1.5 py-0.5 text-[11px] font-semibold leading-none">
                    {selectedRows.length}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-1">
          <Card className="overflow-hidden border-none shadow-sm transition-shadow hover:shadow-md">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Total SMS Events</p>
                  <div className="flex items-baseline gap-2">
                    <h2 className="text-3xl font-bold text-gray-900">{smsPagination.total}</h2>
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                      Recorded
                    </span>
                  </div>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                  <MessageSquare className="h-6 w-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="border-b bg-white p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by phone, user..."
                className="border-gray-200 bg-gray-50 pl-9 transition-colors focus:bg-white"
              />
            </div>

            <div className="flex items-center gap-3">
              <Button variant="outline" size="icon" className="h-10 w-10 text-muted-foreground">
                <Filter className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between border-b bg-gray-50/40 p-4 text-xs text-muted-foreground">
          <span>Showing {smsLogs.length} recent records</span>
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-primary" /> Live
          </span>
        </div>

        <AntDTable
          columns={smsColumns}
          data={smsLogs}
          loading={loading}
          showSettings
          pagination={{
            current: smsPagination.current,
            pageSize: smsPagination.pageSize,
            total: smsPagination.total,
            onChange: handlePageChange,
            showSizeChanger: true,
          }}
          rowKey="id"
          rowSelection={{
            selectedRowKeys,
            onChange: onSelectChange,
          }}
          className="border-0"
          onRow={(record) => ({
            onClick: () => setSelectedLog(record),
            className: "cursor-pointer transition-colors hover:bg-gray-50",
          })}
        />
      </div>

      <CommonModal
        isOpen={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        width="max-w-3xl w-full"
        height="h-auto"
        padding="p-0"
      >
        <div className="flex flex-col">
          <div className="border-b p-6 pb-4">
            <div className="mb-2 flex items-center gap-4">
              <div className="rounded-full bg-primary/10 p-2 text-primary">
                <MessageSquare className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">SMS Log Details</h2>
                <p className="text-sm text-muted-foreground">
                  {selectedLog && formatDate(selectedLog.created_on)}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 gap-8 text-sm md:grid-cols-2">
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900">Message Context</h4>
                <div className="grid grid-cols-[120px_1fr] gap-3">
                  <span className="text-muted-foreground">Phone:</span>
                  <span className="font-medium text-gray-900">
                    {selectedLog?.phone_number ?? "N/A"}
                  </span>

                  <span className="text-muted-foreground">Template:</span>
                  <span>{selectedLog?.template_name ?? "N/A"}</span>

                  <span className="text-muted-foreground">Provider:</span>
                  <span>{selectedLog?.provider ?? "N/A"}</span>

                  <span className="text-muted-foreground">Status:</span>
                  <span>{selectedLog?.status ?? "N/A"}</span>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900">Owner</h4>
                <div className="grid grid-cols-[120px_1fr] gap-3">
                  <span className="text-muted-foreground">User:</span>
                  <span>{selectedLog?.user ?? "N/A"}</span>

                  <span className="text-muted-foreground">Email:</span>
                  <span>{selectedLog?.user_email ?? "N/A"}</span>

                  <span className="text-muted-foreground">Tenant:</span>
                  <span>{selectedLog?.tenant_name ?? selectedLog?.tenant ?? "N/A"}</span>
                </div>
              </div>
            </div>

            <div className="mt-6 space-y-2">
              <h4 className="font-semibold text-gray-900">Message Body</h4>
              <div className="max-h-60 overflow-auto whitespace-pre-wrap rounded-lg border bg-gray-50 p-4 text-sm text-gray-700">
                {selectedLog?.message_body ?? "No message body available"}
              </div>
            </div>

            {selectedLog?.failure_reason && (
              <div className="mt-6 flex gap-3 rounded-lg border border-red-100 bg-red-50 p-4 text-red-800">
                <AlertCircle className="h-5 w-5 shrink-0" />
                <div className="space-y-1">
                  <p className="text-sm font-semibold">Delivery Failure</p>
                  <p className="text-sm opacity-90">{selectedLog.failure_reason}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </CommonModal>
    </>
  );
};

export default SmsLogs;
